import { partition, uniqueId } from 'lodash';
import { stringify } from '../../utils';
import { Entries } from '../../utils/entries';
import { callWith, peek, spread, toEntry } from '../../utils/mappers';
import { isTypeOf } from '../../utils/primitives';
import { tryOrHandle } from '../../utils/throwable';
import { DependencyResolution, DependencyResolutionError, } from './provider/resolution';
import * as Result from './provider/result';
export { Result };
export class DependenciesProvider {
    constructor(factories, logger) {
        this.factories = factories;
        this.logger = logger;
        this.getDependenciesProvider = (name, versions) => {
            const execution = new DependencyResolution(name);
            if (name in this.factories) {
                return this.provideVersions(execution, this.sanitizeVersions(versions));
            }
            return () => execution
                .addIssue(new DependencyResolutionError([name], `dependency not found in provider which only contains: "${Object.keys(this.factories).join('", "')}"`))
                .result({});
        };
        this.provideVersions = (execution, sanitizeVersions) => (providerContext) => execution.result(Entries.toObj(Array.from(sanitizeVersions(execution)).map(toEntry({
            key: version => version,
            value: this.provideVersion(execution, providerContext),
        }))));
        this.provideVersion = (resolution, providerContext) => (version) => {
            if (version in this.factories[resolution.dependencyName]) {
                return tryOrHandle(() => this.factories[resolution.dependencyName][version](providerContext), (cause) => {
                    resolution.addIssue(new DependencyResolutionError([resolution.dependencyName, version], 'an error occurred while building the dependency', { cause }));
                    return undefined;
                });
            }
            resolution.addIssue(new DependencyResolutionError([resolution.dependencyName, version], 'dependency version not provided'));
            return undefined;
        };
        this.logErrors = (aggregated, correlationId) => [aggregated, ...aggregated.errors]
            .flatMap(rootErr => {
            rootErr.message += ` [${correlationId}]`;
            const logged = [rootErr];
            const linkCause = (base) => {
                if (base.cause) {
                    logged.push(rootErr.cause);
                    if (base.cause instanceof Error) {
                        rootErr.message += `\n    Caused by: ${rootErr.cause.message}`;
                        base.cause.message += ` [${correlationId}]`;
                        linkCause(base.cause);
                    }
                }
            };
            linkCause(rootErr);
            return logged;
        })
            .map(e => this.logger.warn(e));
    }
    provide(requested, context) {
        if (!context.targetElement) {
            context.targetElement = {
                // TODO: fix register event v2 uplevel does not propagate dispatched target element
                nodeName: '<unknown target>',
            };
        }
        // Let's use an almost unique id for now and readjust later on
        const correlationId = uniqueId(`${Date.now()}-`);
        // Logged request could help try and debug potential edge cases
        this.logger.info(`[${correlationId}] dependency injection request: %s`, stringify.json({ requested, context }, { space: 4 }));
        return Entries.of(requested)
            .map(spread(this.getDependenciesProvider))
            .map(callWith(context))
            .map(peek(({ errors, name }) => errors.length && this.logErrors(new AggregateError(errors, `${errors.length} issues occurred while getting the requested "${String(name)}" dependency`), correlationId)))
            .map(({ result }) => result)
            .reduce(Result.combine, Result.success({}));
    }
    sanitizeVersions(versions) {
        return (execution) => {
            const versionsError = (explanation) => new DependencyResolutionError([execution.dependencyName], `expected requested versions ${explanation}`);
            if (!Array.isArray(versions)) {
                execution.addIssue(versionsError(`to be an array but received "${String(versions)}" instead`));
                return new Set();
            }
            const [propertyKeys, unsupportedValue,] = partition(versions, isTypeOf('string', 'number', 'symbol'));
            if (unsupportedValue.length) {
                execution.addIssue(versionsError(`to be strings, numbers or symbols but received these unsupported values: "${unsupportedValue.join('", "')}"`));
            }
            return new Set(propertyKeys);
        };
    }
}
//# sourceMappingURL=provider.js.map